import React, { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/compat/router';
import { useObserver } from 'mobx-react';

import useUser from 'hooks/use-user';
import useErnie from 'shared/hooks/use-ernie';
import useUI from 'hooks/use-ui';
import EmptyLoadingState from 'components/empty-loading-state';
import IframeHeightOverride from 'components/core/iframe-height-override';
import useEnterpriseDutchieLoyalty from 'hooks/use-enterprise-dutchie-loyalty';
import { useLoyaltyEnrollmentAnalytics } from 'src/hooks/use-loyalty-enrollment-analytics';
import { Integrations } from 'src/components/loyalty/use-loyalty/types';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useGetDutchiePosLoyaltyConfig } from 'src/components/loyalty/use-get-dutchie-pos-loyalty-config';
import { useGetRewards } from 'src/components/loyalty/use-get-rewards';
import { isEmbeddedLoyaltySignupPopupFlow } from 'src/oauth/embedded-loyalty-signup-popup';
import { useSearchEnterprise } from '../hooks/use-search-enterprise';
import { useEarlyThemeDispensary } from '../hooks/use-early-theme-dispensary';
import { useEnterpriseSignupCustomBranding } from '../hooks/use-enterprise-signup-custom-branding';
import { LandingPage } from './components/landing-page';
import { LoginMethodSelection } from './components/login-method-selection';
import { EnterpriseSignupContent } from './components/enterprise-signup-content';
import { LoyaltyRewards } from './components/loyalty-rewards';
import type { BrandingStatus, EnterpriseSignupProps, FlowStep } from './types';
import { FLOW_STEPS } from './types';

const BRANDING_DECISION_TIMEOUT_MS = 1500;
const GLOBAL_ENROLLMENT_FORM_SOURCE_ID = 5;

export function EnterpriseSignup({ visitorGeo = null }: EnterpriseSignupProps = {}): JSX.Element {
  const router = useRouter();
  const { enterpriseId, cName, state: stateFromUrl, embed: embedParam } = router?.query ?? {};
  const embedParamString = Array.isArray(embedParam) ? embedParam[0] : embedParam;
  const isEmbedMode = embedParamString === '1';
  const User = useUser();
  const showErnie = useErnie();
  const UI = useUI();
  const isIframe = useObserver(() => UI.isIframe);
  // POS-23112 -- the single opt-in for this route's Google SSO popup handoff.
  const isEmbeddedLoyaltySignupPopup = isEmbeddedLoyaltySignupPopupFlow({
    pathname: router?.pathname,
    isEmbedMode,
    isIframe: Boolean(isIframe),
  });

  // Flow state - 4 step flow: landing -> login-method -> email-signup -> loyalty-rewards
  const [currentStep, setCurrentStep] = useState<FlowStep>(FLOW_STEPS.LANDING);
  const [selectedState, setSelectedState] = useState('');
  const [canonicalEnterpriseId, setCanonicalEnterpriseId] = useState('');
  // POS-17999 -- best-guess region for theming only, known before the user confirms a state.
  const [detectedState, setDetectedState] = useState('');
  // Starts loading before state detection or the flag query resolves. The first terminal result
  // (custom, flag-off/error fallback, or timeout fallback) is frozen for this enterprise so a
  // late theme response cannot restyle controls already shown to the shopper.
  const [brandingStatus, setBrandingStatus] = useState<BrandingStatus>('loading');
  const brandingEnterpriseIdRef = useRef<string | null>(null);

  const [loyaltyEnrollmentComplete, setLoyaltyEnrollmentComplete] = useState(false);
  const [enrollmentAttempted, setEnrollmentAttempted] = useState(false);

  // EmailCodeLoginForm logs new users in (isLoggedIn/userId flip true) at the code-verification
  // step, before they've completed the CompleteAccount profile step (name/phone/birthday/medical
  // card) -- unlike the old SignUpForm, which created the account and collected the profile in one
  // atomic action. Without this, the enrollment effect below (which only checked isLoggedIn) fired
  // the instant a new user verified their code, enrolling them with an empty profile and yanking
  // them out of the profile step before they ever saw it. requiresLoginFlow/loginFlowComplete track
  // whether the user actually had to go through this page's own login flow -- if they were already
  // logged in when they clicked "start earning rewards", there's no profile step to wait for, so
  // enrollment proceeds exactly as before.
  const [requiresLoginFlow, setRequiresLoginFlow] = useState(false);
  const [loginFlowComplete, setLoginFlowComplete] = useState(false);
  // POS-23112 -- the handoff payload carries no provider, so the button just pressed is the only
  // signal of which provider is coming back. Both providers report, so a cancelled Google attempt
  // followed by Apple cannot leave 'google' behind.
  const [pendingSocialProvider, setPendingSocialProvider] = useState<'apple' | 'google' | null>(null);

  const isLoggedIn = useObserver(() => User.isLoggedIn);
  // User.loading is boolean | null, initialized to null and only flipped to true inside a
  // useEffect in use-user-controller -- so on the very first render it's still null (falsy),
  // which would let the landing page render as if the user were already resolved. Normalize to
  // "unresolved" (matches the established pattern in post-signup-oauth-trigger.tsx).
  const userLoading = useObserver(() => User.loading) ?? true;
  const userId = useObserver(() => User.id);

  const { trackViewed, trackSubmitted, trackSucceeded, trackFailed } = useLoyaltyEnrollmentAnalytics({
    surface: 'enterpriseSignup',
    integration: Integrations.dutchiepos,
  });
  const hasTrackedViewedRef = useRef(false);
  useEffect(() => {
    if (hasTrackedViewedRef.current) {
      return;
    }
    hasTrackedViewedRef.current = true;
    trackViewed();
  }, [trackViewed]);

  // Convert enterpriseId (or cName slug) to string for template usage
  const enterpriseIdRaw = enterpriseId ?? cName ?? '';
  const enterpriseIdString = Array.isArray(enterpriseIdRaw) ? enterpriseIdRaw[0] : enterpriseIdRaw;

  const { enterprise, loading: enterpriseLoading, error: enterpriseError } = useSearchEnterprise({
    enterpriseId: enterpriseIdString,
  });

  // LaunchDarkly targets the canonical enterprise UUID, not the route slug. The enterprise
  // settings query is required for the page anyway; styling remains optional and bounded.
  const { customBrandingEnabled, loading: customBrandingQueryLoading } = useEnterpriseSignupCustomBranding({
    enterpriseId: enterprise?.enterpriseId,
  });
  const customBrandingFlagLoading = enterpriseLoading || customBrandingQueryLoading;

  useEffect(() => {
    if (enterprise?.uniqueName && enterpriseIdString !== enterprise.uniqueName) {
      const slug = enterprise.uniqueName;
      const isStoresEnterpriseRoute = router?.pathname.startsWith('/stores/enterprise/');
      const isEnterpriseSignupRoute = router?.pathname.startsWith('/enterprise/');
      // Preserve embed=1 through the canonical-slug redirect — otherwise isEmbedMode would flip
      // to false mid-flow, dropping resize wrapping and falling back to top-level navigation.
      const embedSuffix = isEmbedMode ? '?embed=1' : '';

      if (isStoresEnterpriseRoute) {
        void router?.replace(`/stores/enterprise/${slug}/signup${embedSuffix}`, undefined, { shallow: true });
      } else if (isEnterpriseSignupRoute) {
        void router?.replace(`/enterprise/${slug}/signup${embedSuffix}`, undefined, { shallow: true });
      }
    }
    if (enterprise?.enterpriseId && enterpriseIdString !== enterprise.enterpriseId) {
      setCanonicalEnterpriseId(enterprise.enterpriseId);
    }
  }, [enterprise?.uniqueName, enterprise?.enterpriseId, enterpriseId, enterpriseIdString, router, isEmbedMode]);

  useEffect(() => {
    if (router?.isReady && stateFromUrl) {
      const stateParam = Array.isArray(stateFromUrl) ? stateFromUrl[0] : stateFromUrl;
      if (stateParam && stateParam !== selectedState) {
        setSelectedState(stateParam);
      }
    }
  }, [router?.isReady, stateFromUrl, selectedState]);

  const {
    createEnterpriseDutchiePosLoyaltyMember,
    loading: loyaltyLoading,
    error: loyaltyError,
  } = useEnterpriseDutchieLoyalty();

  // Use canonical enterprise ID if available, otherwise use the one from URL
  const activeEnterpriseId = canonicalEnterpriseId || enterpriseIdString;

  // Handle loyalty enrollment errors
  useEffect(() => {
    if (loyaltyError) {
      console.error('Loyalty enrollment error:', loyaltyError);
      showErnie('Error enrolling in rewards program. You can try again later.', 'danger');
      if (isEmbedMode) {
        UI.embeddedEventDispatcher('loyalty:error', { message: loyaltyError.message });
      }
    }
  }, [loyaltyError, showErnie, isEmbedMode, UI]);

  const phone = useObserver(() => User.phone);
  const birthday = useObserver(() => User.birthday);
  // POS-23112 -- only read by the login-step guard below.
  const firstName = useObserver(() => User.firstName);
  const lastName = useObserver(() => User.lastName);
  const readyToEnroll = isLoggedIn && (!requiresLoginFlow || loginFlowComplete);
  const skip = !selectedState || (!userId && !birthday && !isLoggedIn);
  const skipRewards = skip || !phone;
  // Nothing to configure until the profile exists, so wait for the login flow -- just as
  // skipRewards waits for a phone number. Running this mid-flow is what unmounted the form.
  const skipLoyaltyConfig = skip || (requiresLoginFlow && !loginFlowComplete);

  const { data: rewards, loading: rewardsDataLoadingResult, refetch: refetchRewards } = useGetRewards({
    enterpriseId: activeEnterpriseId,
    isoRegionCode: selectedState ? `US-${selectedState}` : undefined,
    phone: phone ?? '',
    birthday: birthday ?? '',
    pin: 'skip',
    customerId: userId,
    skip: skipRewards,
  });

  const {
    data: loyaltyConfigData,
    loading: loyaltyConfigLoadingResult,
    refetch: refetchLoyaltyConfig,
  } = useGetDutchiePosLoyaltyConfig({
    enterpriseId: activeEnterpriseId,
    isoRegionCode: selectedState ? `US-${selectedState}` : undefined,
    skip: skipLoyaltyConfig,
  });

  const dispensaryId = loyaltyConfigData?.dispensaryId;
  const { dispensary: themeDispensary } = useDispensary(dispensaryId ?? undefined);

  // POS-17999 -- themeDispensary above needs a confirmed state + known user, so it's never
  // available on first render. This fills that gap from detectedState, and steps aside once
  // themeDispensary resolves.
  const earlyThemeDispensary = useEarlyThemeDispensary({
    enterpriseId: activeEnterpriseId,
    regionCode: detectedState,
    // Skip the lookup entirely when the flag is off for this enterprise -- no real branding to
    // resolve, so no reason to make the extra network call at all.
    enabled: customBrandingEnabled && !themeDispensary,
  });
  // Preserve the authoritative dispensary regardless of the rollout flag; only the new early
  // lookup is gated. Downstream signup flow already relied on this once the real config resolved.
  const effectiveThemeDispensary = themeDispensary ?? earlyThemeDispensary;

  useEffect(() => {
    const enterpriseIdForBranding = enterprise?.enterpriseId ?? null;
    if (!enterpriseIdForBranding) {
      return;
    }
    if (brandingEnterpriseIdRef.current && brandingEnterpriseIdRef.current !== enterpriseIdForBranding) {
      setBrandingStatus('loading');
    }
    brandingEnterpriseIdRef.current = enterpriseIdForBranding;
  }, [enterprise?.enterpriseId]);

  useEffect(() => {
    if (brandingStatus !== 'loading' || customBrandingFlagLoading) {
      return;
    }
    if (!customBrandingEnabled) {
      setBrandingStatus('fallback');
    } else if (effectiveThemeDispensary) {
      setBrandingStatus('custom');
    }
  }, [brandingStatus, customBrandingEnabled, customBrandingFlagLoading, effectiveThemeDispensary]);

  useEffect(() => {
    if (brandingStatus !== 'loading' || !enterprise?.enterpriseId) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setBrandingStatus((current) => (current === 'loading' ? 'fallback' : current));
    }, BRANDING_DECISION_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [brandingStatus, enterprise?.enterpriseId]);

  const hasFiredLoadedEventRef = useRef(false);

  useEffect(() => {
    if (isEmbedMode && !hasFiredLoadedEventRef.current) {
      hasFiredLoadedEventRef.current = true;
      UI.embeddedEventDispatcher('loyalty:loaded', {});
    }
  }, [isEmbedMode, UI]);

  useEffect(() => {
    if (effectiveThemeDispensary) {
      UI.setEnterpriseDispensary(effectiveThemeDispensary);
    }
    return () => {
      UI.setEnterpriseDispensary(null);
    };
  }, [UI, effectiveThemeDispensary]);

  // Refetch hooks when selectedState changes
  useEffect(() => {
    if (userId && selectedState && isLoggedIn && phone && birthday) {
      void refetchRewards();
      void refetchLoyaltyConfig();
    }
  }, [selectedState, isLoggedIn, refetchRewards, refetchLoyaltyConfig, userId, phone, birthday]);

  // Enroll user in loyalty when they become logged in and have selected a state
  useEffect(() => {
    const enrollUserInLoyalty = async (): Promise<void> => {
      if (
        !readyToEnroll ||
        !userId ||
        !selectedState ||
        userLoading ||
        loyaltyLoading ||
        loyaltyEnrollmentComplete ||
        enrollmentAttempted ||
        enterpriseLoading ||
        !enterprise
      ) {
        return;
      }
      setEnrollmentAttempted(true);

      // If user is already a loyalty member, set the enrollment complete flag and show the success message
      if (rewards?.getRewardsV2?.isLoyaltyMember && loyaltyConfigData) {
        setLoyaltyEnrollmentComplete(true);
        showErnie(`Welcome to ${String(enterprise.name || 'the')} rewards program!`, 'success');
        // Move to loyalty-rewards and enroll
        if (currentStep !== FLOW_STEPS.LOYALTY_REWARDS) {
          setCurrentStep(FLOW_STEPS.LOYALTY_REWARDS);
        }
      }

      if (!rewards?.getRewardsV2?.isLoyaltyMember && loyaltyConfigData) {
        trackSubmitted();
        try {
          // request rewards and loylatyConfig after each state selection and then run this call or not

          await createEnterpriseDutchiePosLoyaltyMember({
            variables: {
              enterpriseId: activeEnterpriseId,
              userId,
              isoRegionCode: `US-${selectedState}`,
              loyaltyRegistrationSourceId: GLOBAL_ENROLLMENT_FORM_SOURCE_ID,
            },
          });
          void refetchRewards();

          trackSucceeded();
          setLoyaltyEnrollmentComplete(true);
          showErnie(`Welcome to ${String(enterprise.name || 'the')} rewards program!`, 'success');

          // Move to loyalty-rewards and enroll
          if (currentStep !== FLOW_STEPS.LOYALTY_REWARDS) {
            setCurrentStep(FLOW_STEPS.LOYALTY_REWARDS);
          }
        } catch (error) {
          trackFailed();
          // Don't dispatch loyalty:error here — createEnterpriseDutchiePosLoyaltyMember failing
          // also populates loyaltyError below, which already dispatches. Dispatching here too
          // would send the embedding parent two error events for one failure.
          console.error('Error enrolling in enterprise loyalty:', error);
          showErnie('Error enrolling in rewards program. You can try again later.', 'danger');
        }
      }
    };

    if (userLoading) {
      return;
    }
    if (
      readyToEnroll &&
      selectedState &&
      !loyaltyEnrollmentComplete &&
      !enrollmentAttempted &&
      !rewardsDataLoadingResult &&
      !loyaltyConfigLoadingResult
    ) {
      void enrollUserInLoyalty();
    }
  }, [
    loyaltyConfigData,
    rewards?.getRewardsV2,
    rewardsDataLoadingResult,
    loyaltyConfigLoadingResult,
    readyToEnroll,
    userLoading,
    userId,
    selectedState,
    currentStep,
    loyaltyLoading,
    loyaltyEnrollmentComplete,
    enrollmentAttempted,
    enterpriseLoading,
    enterprise,
    activeEnterpriseId,
    createEnterpriseDutchiePosLoyaltyMember,
    showErnie,
    refetchRewards,
    trackSubmitted,
    trackSucceeded,
    trackFailed,
  ]);

  const handleStartEarningRewards = (stateCode: string): void => {
    setSelectedState(stateCode);

    if (router) {
      void router.push(
        {
          pathname: router.pathname,
          query: { ...router.query, state: stateCode },
        },
        undefined,
        { shallow: true }
      );
    }

    // If not logged in, go to login flow
    // If already logged in, the enrollment effect will handle moving to loyalty-rewards
    if (!isLoggedIn) {
      setRequiresLoginFlow(true);
      setCurrentStep(FLOW_STEPS.LOGIN_METHOD);
    }
  };

  const handleContinueWithEmail = (): void => {
    setCurrentStep(FLOW_STEPS.EMAIL_SIGNUP);
  };

  const handleLoginFlowSuccess = (): void => {
    setLoginFlowComplete(true);
  };

  // POS-23112 -- the embedded social popup delivers its token into this live page instead of
  // navigating, so nothing else reports this step as finished. Every other signup gets that for
  // free: its OAuth callback navigates and remounts this component with requiresLoginFlow false.
  // Both providers mark their popup on this route, and both callbacks round-trip the marker, so
  // either handoff completes the step.
  //
  // Each remaining condition is load-bearing. A social submit must have happened, so the email
  // path cannot trip this. Login-method step only: the email path has already advanced, where
  // CompleteAccount owns the profile. And a net-new account lands here with an incomplete
  // profile, so wait for the four fields `isMissingInfo` in
  // src/components/incomplete-account-detection requires -- keep them in sync with that owner --
  // or enrollment races its modal.
  useEffect(() => {
    if (
      isEmbeddedLoyaltySignupPopup &&
      pendingSocialProvider !== null &&
      currentStep === FLOW_STEPS.LOGIN_METHOD &&
      isLoggedIn &&
      firstName &&
      lastName &&
      birthday &&
      phone
    ) {
      setLoginFlowComplete(true);
    }
  }, [
    isEmbeddedLoyaltySignupPopup,
    pendingSocialProvider,
    currentStep,
    isLoggedIn,
    firstName,
    lastName,
    birthday,
    phone,
  ]);

  let content: JSX.Element;

  // EnterpriseSignupContent keeps `step` in local state, so replacing it with a spinner mid-flow
  // resets the user to the email form. Keyed on currentStep, not loginFlowComplete: only a
  // *successful* enrollment advances the step, so releasing on loginFlowComplete would unmount
  // during enrollment and strand a failed one back on the email form.
  const inLoginFlow =
    requiresLoginFlow && (currentStep === FLOW_STEPS.LOGIN_METHOD || currentStep === FLOW_STEPS.EMAIL_SIGNUP);
  const showLoadingState = rewardsDataLoadingResult || loyaltyConfigLoadingResult || loyaltyLoading;
  const isResolvingLandingUser = currentStep === FLOW_STEPS.LANDING && userLoading;

  if (enterpriseLoading || !enterprise || isResolvingLandingUser || (!inLoginFlow && showLoadingState)) {
    content = <EmptyLoadingState isLoading page='enterprise-signup' />;
  } else if (enterpriseError) {
    console.error('Enterprise error:', {
      enterpriseError,
      enterpriseId: enterpriseIdString,
    });
    content = <EmptyLoadingState isLoading={false} page='enterprise-signup' />;
  } else if (currentStep === FLOW_STEPS.LANDING) {
    content = (
      <LandingPage
        enterprise={enterprise}
        onStartEarningRewards={handleStartEarningRewards}
        onLikelyStateDetected={setDetectedState}
        themeDispensary={effectiveThemeDispensary}
        brandingStatus={brandingStatus}
        visitorGeo={visitorGeo}
      />
    );
  } else if (currentStep === FLOW_STEPS.LOGIN_METHOD) {
    content = (
      <LoginMethodSelection
        onContinueWithEmail={handleContinueWithEmail}
        themeDispensary={effectiveThemeDispensary}
        brandingStatus={brandingStatus}
        isEmbeddedLoyaltySignupPopupFlow={isEmbeddedLoyaltySignupPopup}
        onSocialSubmit={setPendingSocialProvider}
      />
    );
  } else if (currentStep === FLOW_STEPS.EMAIL_SIGNUP) {
    content = (
      <EnterpriseSignupContent dispensaryId={dispensaryId ?? undefined} onLoginSuccess={handleLoginFlowSuccess} />
    );
  } else {
    content = (
      <LoyaltyRewards
        enterprise={enterprise}
        rewards={rewards?.getRewardsV2}
        loyaltyConfigData={loyaltyConfigData}
        rewardsDataLoading={rewardsDataLoadingResult}
        loyaltyConfigLoading={loyaltyConfigLoadingResult}
        isEmbedMode={isEmbedMode}
      />
    );
  }

  return isEmbedMode ? <IframeHeightOverride>{content}</IframeHeightOverride> : content;
}
